import { format, parseISO } from 'date-fns';

export type OrderStage = 'scheduled' | 'preparing' | 'delivering' | 'arrived';

interface OrderStatusProps {
  stage: OrderStage;
  estimatedTime: string;
  date?: string;
  arrivedTime?: string;
}

export default function OrderStatus({ stage, estimatedTime, date, arrivedTime }: OrderStatusProps) {
  const getStatusTitle = () => {
    switch (stage) {
      case 'scheduled':
        return 'On schedule';
      case 'preparing':
        return 'Picking up your order...';
      case 'delivering':
        return 'Your order is on the way';
      case 'arrived':
        return 'Order has been arrived';
      default:
        return 'On schedule';
    }
  };

  const formatScheduledDate = (dateString?: string) => {
    if (!dateString) return '';
    try {
      const date = parseISO(dateString);
      return format(date, 'dd MMM yyyy');
    } catch (error) {
      console.error('Error formatting date:', error);
      return '';
    }
  };

  const formatArrivedTime = (dateString?: string) => {
    if (!dateString) return '';
    try {
      const date = parseISO(dateString);
      return format(date, 'HH:mm');
    } catch (error) {
      console.error('Error formatting arrived time:', error);
      return '';
    }
  };

  const getStatusMessage = () => {
    switch (stage) {
      case 'scheduled': {
        const formattedDate = formatScheduledDate(date);
        return `${formattedDate}, ${estimatedTime}`;
      }
      case 'preparing':
        return 'Our driver is picking up your flowers from the shop.';
      case 'delivering':
        return 'Your flowers are on their way to you.';
      case 'arrived':
        return 'Your flowers have been delivered successfully.';
      default:
        return '';
    }
  };

  const getProgressWidth = () => {
    switch (stage) {
      case 'scheduled':
        return 'w-0';
      case 'preparing':
        return 'w-1/3';
      case 'delivering':
        return 'w-2/3';
      case 'arrived':
        return 'w-full';
      default:
        return 'w-0';
    }
  };

  return (
    <div className="flex flex-col space-y-2">
      <h1 className="text-2xl font-semibold">{getStatusTitle()}</h1>
      {stage === 'scheduled' ? (
        <p className="text-gray-700">{getStatusMessage()}</p>
      ) : (
        <p className="text-gray-700">
          {stage === 'arrived' ? 
            `Arrived at: ${formatArrivedTime(arrivedTime)}` : 
            `Estimated arrival: ${estimatedTime}`}
        </p>
      )}
      {stage !== 'scheduled' && (
        <div className="relative h-1 bg-gray-200 rounded-full mt-2">
          <div 
            className={`absolute h-full bg-amber-400 rounded-full transition-all duration-500 ease-in-out ${getProgressWidth()}`}
          />
        </div>
      )}
      {stage !== 'scheduled' && (
        <p className="text-gray-500 text-sm mt-1">
          {getStatusMessage()}
        </p>
      )}
    </div>
  );
} 